import meta from "./head.json"

// The car-view brain's own layer: seven image embeddings of the car (whole,
// left and right halves, four quarters left to right) in, one probability per
// view out. Trained by scripts/car-brain/train.py on hand-labelled real
// photos (scripts/car-brain/REPORT.md); the weights live next to the models
// in public/mediapipe/car-view-head.bin. Pure; unit-tested against Python.

export type ViewClass =
  | "front"
  | "rear"
  | "left"
  | "right"
  | "FL"
  | "FR"
  | "RL"
  | "RR"
  | "closeup"
  | "sticker"
  | "other"

export const VIEW_CLASSES = meta.classes as readonly ViewClass[]
// Versioned: /mediapipe/** is cached for a year, and these weights change
// whenever the brain is retrained (head.json ships in the bundle).
export const HEAD_URL = `/mediapipe/car-view-head.bin?v=${meta.version}`
export const EMBEDDER_URL = `/mediapipe/${meta.embedder}`

export interface ViewHead {
  featureDim: number
  // [featureDim][classes], row-major.
  weights: Float32Array
  bias: Float32Array
}

export interface Embeddings {
  whole: ArrayLike<number>
  left: ArrayLike<number>
  right: ArrayLike<number>
  // Four quarters of the car, left to right.
  quarters: readonly [ArrayLike<number>, ArrayLike<number>, ArrayLike<number>, ArrayLike<number>]
}

export interface CarGeometry {
  hasCar: boolean
  score: number
  // Width over height of the car's box, in pixels.
  aspect: number
  // The box's share of the photo (0–1).
  area: number
}

function halfToFloat(h: number): number {
  const sign = h & 0x8000 ? -1 : 1
  const exp = (h >> 10) & 0x1f
  const frac = h & 0x3ff
  if (exp === 0) return sign * 2 ** -14 * (frac / 1024)
  if (exp === 0x1f) return frac ? NaN : sign * Infinity
  return sign * 2 ** (exp - 15) * (1 + frac / 1024)
}

export function decodeFloat16(bytes: Uint8Array): Float32Array {
  const out = new Float32Array(bytes.length / 2)
  for (let i = 0; i < out.length; i++) out[i] = halfToFloat(bytes[2 * i] | (bytes[2 * i + 1] << 8))
  return out
}

// The .bin: float16 weights [featureDim][classes], then float32 bias[classes].
export function parseHead(buffer: ArrayBuffer): ViewHead {
  const k = VIEW_CLASSES.length
  const featureDim = meta.featureDim
  const wBytes = featureDim * k * 2
  if (buffer.byteLength !== wBytes + k * 4) throw new Error("car-view head: unexpected size")
  return {
    featureDim,
    weights: decodeFloat16(new Uint8Array(buffer, 0, wBytes)),
    bias: new Float32Array(buffer.slice(wBytes)),
  }
}

// Same order as train.py features(kind="quarters").
export function buildFeatures(e: Embeddings, geo: CarGeometry): Float32Array {
  const dim = e.whole.length
  const out = new Float32Array(dim * 10 + 4)
  const put = (block: number, f: (i: number) => number) => {
    for (let i = 0; i < dim; i++) out[block * dim + i] = f(i)
  }
  const [q0, q1, q2, q3] = e.quarters
  put(0, (i) => e.whole[i])
  put(1, (i) => e.left[i])
  put(2, (i) => e.right[i])
  put(3, (i) => e.left[i] - e.right[i])
  put(4, (i) => q0[i])
  put(5, (i) => q1[i])
  put(6, (i) => q2[i])
  put(7, (i) => q3[i])
  put(8, (i) => q0[i] - q3[i])
  put(9, (i) => q1[i] - q2[i])
  const g = dim * 10
  out[g] = geo.hasCar ? 1 : 0
  out[g + 1] = geo.score
  out[g + 2] = Math.min(geo.aspect, 4) / 4
  out[g + 3] = geo.area
  return out
}

export type ViewProbs = Record<ViewClass, number>

export function headProbs(head: ViewHead, features: ArrayLike<number>): ViewProbs {
  const k = VIEW_CLASSES.length
  if (features.length !== head.featureDim) throw new Error("car-view head: wrong feature length")
  const logits = Float64Array.from(head.bias)
  for (let i = 0; i < head.featureDim; i++) {
    const x = features[i]
    if (x === 0) continue
    const row = i * k
    for (let j = 0; j < k; j++) logits[j] += x * head.weights[row + j]
  }
  const max = Math.max(...logits)
  let sum = 0
  for (let j = 0; j < k; j++) {
    logits[j] = Math.exp(logits[j] - max)
    sum += logits[j]
  }
  const probs = {} as ViewProbs
  VIEW_CLASSES.forEach((c, j) => (probs[c] = logits[j] / sum))
  return probs
}

export function topView(probs: ViewProbs): { view: ViewClass; p: number } {
  let view: ViewClass = "other"
  let p = -1
  for (const c of VIEW_CLASSES) {
    if (probs[c] > p) {
      p = probs[c]
      view = c
    }
  }
  return { view, p }
}
